import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Archive, Calendar } from 'lucide-react';
import { useEpicList, useCreateEpic, useCloseEpic, useFlaviaConfigList } from '@/hooks/usePerformance';
import { EpicTypeBadge } from '@/components/performance/EpicTypeBadge';
import { format, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { WinclapLoader } from '@/components/ui/winclap-loader';

export function PerformanceAdminEpics() {
  const { t } = useTranslation();
  const { data: epics, isLoading } = useEpicList();
  const { data: assistants } = useFlaviaConfigList();
  const createEpic = useCreateEpic();
  const closeEpic = useCloseEpic();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', startDate: '', endDate: '', assistantId: '' });

  async function handleCreate() {
    if (!form.name || !form.startDate || !form.endDate) {
      toast.error(t('performance.admin.epics.complete_fields'));
      return;
    }
    await createEpic.mutateAsync({
      name: form.name,
      epicType: 'seasonal',
      startDate: form.startDate,
      endDate: form.endDate,
      dimensions: [],
      assistantId: form.assistantId || 'default',
    });
    setShowCreate(false);
    setForm({ name: '', startDate: '', endDate: '', assistantId: '' });
  }

  const epicList = (epics as any[] ?? []);
  const activeEpics = epicList.filter((e) => e.status === 'active');
  const closedEpics = epicList.filter((e) => e.status !== 'active');

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">{t('performance.admin.layout.epics')}</h1>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          {t('performance.adminepics.nuevo_epic')}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <WinclapLoader size={32} className="text-foreground/25" />
        </div>
      ) : epicList.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 text-muted-foreground text-sm">
            {t('performance.adminepics.no_hay_epics_creados')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Active EPICs — destacados */}
          {activeEpics.map((epic) => {
            const start = new Date(epic.startDate);
            const end = new Date(epic.endDate);
            const total = differenceInDays(end, start);
            const elapsed = differenceInDays(new Date(), start);
            const progress = total === 0 ? 100 : Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));

            return (
              <Card key={epic.uuid} className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-950/10">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{epic.name}</span>
                        <EpicTypeBadge type={epic.epicType ?? 'seasonal'} />
                        <span className="text-xs px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-400 dark:border-emerald-700">
                          activo
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {epic.startDate && format(new Date(epic.startDate), 'dd/MM/yyyy')} — {epic.endDate && format(new Date(epic.endDate), 'dd/MM/yyyy')}
                      </div>
                      <div className="mt-3 space-y-1">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{t('performance.adminepics.progreso_del_ciclo')}</span>
                          <span>{progress}%</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-emerald-200 dark:bg-emerald-800">
                          <div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void closeEpic.mutateAsync({ id: epic.uuid })}
                      disabled={closeEpic.isPending}
                      className="shrink-0"
                    >
                      <Archive className="h-3.5 w-3.5 mr-1.5" />
                      Cerrar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Closed EPICs — apagados */}
          {closedEpics.length > 0 && (
            <div className="space-y-2">
              {activeEpics.length > 0 && (
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-2">Cerrados</p>
              )}
              {closedEpics.map((epic) => (
                <Card key={epic.uuid} className="opacity-60">
                  <CardContent className="flex items-center gap-4 pt-3 pb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{epic.name}</span>
                        <EpicTypeBadge type={epic.epicType ?? 'seasonal'} />
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {epic.startDate && format(new Date(epic.startDate), 'dd/MM/yyyy')} — {epic.endDate && format(new Date(epic.endDate), 'dd/MM/yyyy')}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('performance.adminepics.nuevo_epic_seasonal')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nombre</Label>
              <Input
                className="mt-1"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder={t('performance.admin.epics.name_example')}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('performance.adminepics.fecha_inicio')}</Label>
                <Input className="mt-1" type="date" value={form.startDate} onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))} />
              </div>
              <div>
                <Label>{t('performance.adminepics.fecha_fin')}</Label>
                <Input className="mt-1" type="date" value={form.endDate} onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Asistente</Label>
              <Select value={form.assistantId} onValueChange={(v) => setForm((p) => ({ ...p, assistantId: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('performance.adminepics.selecciona_un_asistente')} />
                </SelectTrigger>
                <SelectContent>
                  {(assistants as any[] ?? []).map((a: any) => (
                    <SelectItem key={a.uuid} value={a.uuid}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t('common.ui.cancel')}</Button>
            <Button onClick={handleCreate} disabled={createEpic.isPending}>
              {createEpic.isPending ? <WinclapLoader size={16} /> : t('performance.admin.epics.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
